import axios from 'axios';
import { auth } from '../firebase.config';
import { chatConfig } from '../config/chatConfig';

class ChatAPI {
    constructor() {
        this.baseURL = `${chatConfig.backendUrl}/api/v1/chat`;
        this.maxRetries = 3;
        this.retryDelay = 1000; // 1 second base delay
    }

    // Retry utility with exponential backoff
    async retryWithBackoff(fn, maxRetries = this.maxRetries, baseDelay = this.retryDelay) {
        let lastError;
        
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                return await fn();
            } catch (error) {
                lastError = error;
                
                // Don't retry for certain error types
                if (this.shouldNotRetry(error)) {
                    throw error;
                }
                
                // If this is the last attempt, throw the error
                if (attempt === maxRetries) {
                    console.error(`Failed after ${maxRetries + 1} attempts:`, error);
                    throw error;
                }
                
                // Calculate delay with exponential backoff and jitter
                const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
                console.warn(`Attempt ${attempt + 1} failed, retrying in ${Math.round(delay)}ms...`, error.message);
                
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
        
        throw lastError;
    }

    // Determine if an error should not be retried
    shouldNotRetry(error) {
        // Don't retry authentication errors
        if (error.response?.status === 401 || error.response?.status === 403) {
            return true;
        }
        
        // Don't retry user not found errors
        if (error.response?.status === 404 || error.message === 'USER_NOT_FOUND') {
            return true;
        }
        
        // Don't retry validation errors
        if (error.response?.status === 400) {
            return true;
        }
        
        return false;
    }

    async getAuthHeaders() {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('User not authenticated');
            }
            
            const token = await user.getIdToken();
            return {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
        } catch (error) {
            console.error('Failed to get auth headers:', error);
            throw error;
        }
    }

    // Start a new conversation or find existing one
    async startConversation(participantId) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.post(
                `${this.baseURL}/conversations`,
                { participantId },
                { 
                    headers,
                    timeout: chatConfig.apiTimeout
                }
            );
            return response.data;
        } catch (error) {
            console.error('Failed to start conversation:', error);
            
            // If backend is not available, create a mock conversation for demo
            if (error.code === 'ECONNREFUSED' || error.response?.status >= 500 || !error.response) {
                console.log('Backend not available, creating mock conversation');
                return {
                    conversation: {
                        id: `mock_${Date.now()}`,
                        participants: [participantId],
                        lastMessage: null,
                        updatedAt: new Date().toISOString()
                    }
                };
            }
            
            throw error;
        }
    }

    // Find existing conversation between two users
    async findConversation(participantId) {
        console.log(participantId, "This is the participant Id");
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.get(
                `${this.baseURL}/conversations/find/${participantId}`,
                { 
                    headers,
                    timeout: chatConfig.apiTimeout
                }
            );
            return response.data;
        } catch (error) {
            console.error('Failed to find conversation:', error);
            
            // If conversation not found or backend error, return null
            if (error.response?.status === 404 || 
                error.code === 'ECONNREFUSED' || 
                error.response?.status >= 500) {
                return { conversation: null };
            }
            
            throw error;
        }
    }

    // Get or create conversation between two users (ensures uniqueness)
    async getOrCreateConversation(participantId) {
        return await this.retryWithBackoff(async () => {
            try {
                const headers = await this.getAuthHeaders();
                const response = await axios.post(
                    `${this.baseURL}/conversations/get-or-create`,
                    { participantId },
                    { 
                        headers,
                        timeout: chatConfig.apiTimeout
                    }
                );
                return response.data;
            } catch (error) {
                console.error('Failed to get or create conversation:', error);
                
                // Check if user doesn't exist (404 or specific error code)
                if (error.response?.status === 404 || error.response?.data?.error === 'USER_NOT_FOUND') {
                    throw new Error('USER_NOT_FOUND');
                }
                
                // If backend is not available, create a mock conversation for demo
                if (error.code === 'ECONNREFUSED' || error.response?.status >= 500 || !error.response) {
                    console.log('Backend not available, creating mock conversation');
                    return {
                        conversation: {
                            id: `mock_${Date.now()}`,
                            participants: [participantId],
                            lastMessage: null,
                            updatedAt: new Date().toISOString()
                        }
                    };
                }
                
                throw error;
            }
        });
    }

    // Send invitation email to user who doesn't exist
    async sendInvitationEmail(invitedUserData, inviterData) {
        return await this.retryWithBackoff(async () => {
            try {
                const headers = await this.getAuthHeaders();
                const response = await axios.post(
                    `${this.baseURL}/invite-user`,
                    {
                        invitedUser: {
                            name: invitedUserData.name || invitedUserData.fullName,
                            fullName: invitedUserData.fullName,
                            email: invitedUserData.email || null,
                            phoneNumber: invitedUserData.phoneNumber || null
                        },
                        inviter: {
                            name: inviterData.fullName || `${inviterData.firstName} ${inviterData.lastName}`,
                            email: inviterData.email,
                            id: inviterData.id
                        }
                    },
                    { 
                        headers,
                        timeout: chatConfig.apiTimeout
                    }
                );
                return response.data;
            } catch (error) {
                console.error('Failed to send invitation email:', error);
                throw error;
            }
        });
    }

    // Get user's conversations
    async getConversations(limit = 20, offset = 0) {
        return await this.retryWithBackoff(async () => {
            try {
                const headers = await this.getAuthHeaders();
                const response = await axios.get(
                    `${this.baseURL}/conversations?limit=${limit}&offset=${offset}`,
                    { 
                        headers,
                        timeout: chatConfig.apiTimeout
                    }
                );
                return response.data;
            } catch (error) {
                console.error('Failed to get conversations:', error);
                
                // If it's a network error or backend is not available, return empty conversations
                if (error.code === 'ECONNREFUSED' || error.code === 'NETWORK_ERROR' || 
                    error.response?.status >= 500 || !error.response) {
                    console.log('Backend not available, returning empty conversations');
                    return { conversations: [] };
                }
                
                throw error;
            }
        });
    }

    // Get conversation details
    async getConversation(conversationId) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.get(
                `${this.baseURL}/conversations/${conversationId}`,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Failed to get conversation:', error);
            throw error;
        }
    }

    // Get messages for a conversation
    async getMessages(conversationId, limit = 50, offset = 0) {
        return await this.retryWithBackoff(async () => {
            try {
                const headers = await this.getAuthHeaders();
                const response = await axios.get(
                    `${this.baseURL}/conversations/${conversationId}/messages?limit=${limit}&offset=${offset}`,
                    { headers }
                );
                return response.data;
            } catch (error) {
                console.error('Failed to get messages:', error);
                throw error;
            }
        });
    }

    // Send a message (REST fallback)
    async sendMessage(conversationId, content, messageType = 'text') {
        return await this.retryWithBackoff(async () => {
            try {
                const headers = await this.getAuthHeaders();
                const response = await axios.post(
                    `${this.baseURL}/conversations/${conversationId}/messages`,
                    { content, messageType },
                    { headers }
                );
                return response.data;
            } catch (error) {
                console.error('Failed to send message:', error);
                throw error;
            }
        });
    }

    // Mark conversation as read
    async markAsRead(conversationId) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.put(
                `${this.baseURL}/conversations/${conversationId}/read`,
                {},
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Failed to mark as read:', error);
            throw error;
        }
    }

    // Leave conversation
    async leaveConversation(conversationId) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.delete(
                `${this.baseURL}/conversations/${conversationId}/leave`,
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Failed to leave conversation:', error);
            throw error;
        }
    }

    // Archive conversation
    async archiveConversation(conversationId) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.put(
                `${this.baseURL}/conversations/${conversationId}/archive`,
                {},
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Failed to archive conversation:', error);
            throw error;
        }
    }

    // Unarchive conversation
    async unarchiveConversation(conversationId) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await axios.put(
                `${this.baseURL}/conversations/${conversationId}/unarchive`,
                {},
                { headers }
            );
            return response.data;
        } catch (error) {
            console.error('Failed to unarchive conversation:', error);
            throw error;
        }
    }
}

const chatAPI = new ChatAPI();
export default chatAPI;
